import { useState, useEffect, useCallback } from "react";
import { AlertCircle } from "lucide-react";
import { getApiUrl } from "@/config/api";
import { authenticatedFetch } from "@/utils/apiUtils";
import { RiskAlertReview, CoachAction } from "@/types";
import AlertActionModal from "./AlertActionModal";

interface AlertAuditCardProps {
  teamId: number;
}

const ACTION_LABEL: Record<CoachAction, string> = {
  pending: "Pending review",
  no_action: "No action",
  load_adjustment: "Load adjustment",
  medical_check: "Medical check",
  removed_from_practice: "Removed from practice",
  other: "Other",
};

const OUTCOME_BADGE: Record<string, { label: string; color: string }> = {
  unresolved: { label: "7d window open", color: "cgm-text-secondary" },
  injury_logged: { label: "Injury within 7d", color: "text-red-500" },
  no_event: { label: "No event in 7d", color: "text-green-500" },
};

export default function AlertAuditCard({ teamId }: AlertAuditCardProps) {
  const [reviews, setReviews] = useState<RiskAlertReview[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<RiskAlertReview | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await authenticatedFetch<RiskAlertReview[]>(
      getApiUrl(`/api/alert-reviews/?team_id=${teamId}`),
      { method: "GET" },
      "fetching risk alert reviews",
    );
    if (result.success && result.data) {
      setReviews(result.data);
    }
    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="cgm-card p-4 cgm-text-secondary text-sm">Loading alert audit...</div>;
  }
  if (!reviews || reviews.length === 0) return null;

  const pendingCount = reviews.filter((r) => r.coach_action === "pending").length;

  return (
    <div className="cgm-card p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 cgm-text-secondary" />
          <h3 className="text-sm font-semibold cgm-text-primary">Alert Audit</h3>
        </div>
        {pendingCount > 0 && (
          <span className="text-[10px] uppercase tracking-wider text-amber-500">
            {pendingCount} pending
          </span>
        )}
      </div>

      <div className="overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="cgm-text-secondary text-left">
              <th className="py-1 font-normal">Date</th>
              <th className="py-1 font-normal">Athlete</th>
              <th className="py-1 font-normal">Tier</th>
              <th className="py-1 font-normal">Action</th>
              <th className="py-1 font-normal">7d outcome</th>
              <th className="py-1 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((r) => {
              const outcome = OUTCOME_BADGE[r.followup_outcome] ?? OUTCOME_BADGE.unresolved;
              return (
                <tr key={r.id} className="border-t" style={{ borderColor: 'var(--card-border)' }}>
                  <td className="py-1.5 cgm-text-secondary font-mono">{r.alert_date}</td>
                  <td className="py-1.5 cgm-text-primary">{r.athlete_name}</td>
                  <td className="py-1.5 cgm-text-primary">{r.tier}</td>
                  <td className="py-1.5 cgm-text-primary">{ACTION_LABEL[r.coach_action]}</td>
                  <td className={`py-1.5 ${outcome.color}`}>{outcome.label}</td>
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => setSelected(r)}
                      className="text-[11px] underline cgm-text-secondary hover:cgm-text-primary"
                    >
                      {r.coach_action === "pending" ? "Record" : "Edit"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <AlertActionModal
          review={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}
